import { SuspenseSelect } from '@/components/Suspense';
import { useGetInvoiceFilesReportStatuses } from '@/context/services/reportsType/invoiceReports/Edit.service';
import { SearchFormData } from '@/features/approving_invoice_reports/components/Search/hooks/useSearchForm';
import { PropsControl } from '@/features/approving_invoice_reports/components/Search/type';
import { FormSelect } from '@moneyforward/ap-frontend-components';
import { FC, memo, useEffect, useMemo } from 'react';

const _InvoiceMFFileStatusesSelect: FC<PropsControl> = memo(
  ({ control, name, labelProps, formContext }) => {
    const { data } = useGetInvoiceFilesReportStatuses();
    const currentValue = formContext?.getValues(name);

    const memoizedMFFileStatuses = useMemo(() => {
      return (
        data?.data?.map((status) => ({
          label: status.name ?? '',
          value: status.value ?? '',
        })) ?? []
      );
    }, [data?.data]);

    useEffect(() => {
      if (!currentValue || !memoizedMFFileStatuses?.length) return;
      // Check if current value exists in available options
      const isValid = memoizedMFFileStatuses?.some(
        (option) => option.value === currentValue
      );
      // Clear invalid value
      if (!isValid) {
        formContext?.setValue(name, '');
      }
    }, [currentValue, memoizedMFFileStatuses, formContext, name]);

    const selectProps = useMemo(
      () => ({
        options: memoizedMFFileStatuses,
        label: labelProps,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        size: 'small' as const,
      }),
      [labelProps, memoizedMFFileStatuses]
    );

    return (
      <FormSelect<SearchFormData>
        control={control}
        name={name}
        inputId={name}
        testId={name}
        selectProps={selectProps}
      />
    );
  }
);

_InvoiceMFFileStatusesSelect.displayName = '_InvoiceMFFileStatusesSelect';

export const InvoiceMFFileStatusesSelect: FC<PropsControl> = memo((props) => (
  <SuspenseSelect>
    <_InvoiceMFFileStatusesSelect {...props} />
  </SuspenseSelect>
));

InvoiceMFFileStatusesSelect.displayName = 'InvoiceMFFileStatusesSelect';
